import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ThreeBackground } from './ThreeBackground';

export const AuthLayout = ({ children, title, subtitle, image }) => {
    return (
        <div className="min-h-screen w-full flex flex-col md:flex-row bg-space-blue relative">
            <ThreeBackground />
            {/* Left Side - Visual */}
            <div className="relative w-full md:w-1/2 lg:w-5/12 bg-space-blue overflow-hidden min-h-[300px] md:min-h-screen">
                <div className="absolute inset-0 bg-gradient-to-br from-space-blue via-slate-900 to-cyan-950 opacity-90 animate-gradient-x" />

                {/* Abstract Shapes */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
                    <motion.div
                        animate={{
                            y: [0, -20, 0],
                            rotate: [0, 5, 0],
                            scale: [1, 1.05, 1],
                        }}
                        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
                        className="absolute -top-20 -left-20 w-96 h-96 bg-cyan-600/20 rounded-full mix-blend-screen filter blur-3xl opacity-30"
                    />
                    <motion.div
                        animate={{
                            y: [0, 30, 0],
                            rotate: [0, -5, 0],
                            scale: [1, 1.1, 1],
                        }}
                        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                        className="absolute top-1/2 -right-20 w-80 h-80 bg-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30"
                    />
                </div>

                {/* Content Overlay */}
                <div className="relative z-10 h-full flex flex-col justify-between p-8 md:p-12 text-white">
                    <Link to="/" className="flex items-center space-x-2 w-fit group">
                        <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg group-hover:bg-white/30 transition-colors">
                            <ArrowLeft className="w-5 h-5" />
                        </div>
                        <span className="font-medium">Back to Home</span>
                    </Link>

                    <div className="mt-12 md:mt-0">
                        {image ? image : (
                            <div className="mb-8">
                                <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 leading-tight">
                                    Join the future of <br />
                                    <span className="text-cyan-300 text-shadow-glow">Healthcare</span>
                                </h2>
                                <p className="text-white/80 text-lg">
                                    Connect with top employers and take your career to the next
                                    level in Germany.
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="hidden md:block text-sm text-white/50">
                        © {new Date().getFullYear()} Polyglots. All rights reserved.
                    </div>
                </div>
            </div>

            {/* Right Side - Form */}
            <div className="w-full md:w-1/2 lg:w-7/12 flex items-center justify-center p-6 md:p-12 lg:p-20 relative">
                {/* Background glow for form */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full max-w-lg bg-cyan-500/5 blur-[120px] rounded-full pointer-events-none" />

                <div className="w-full max-w-2xl relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="mb-10 text-center md:text-left">
                            <h1 className="text-4xl md:text-5xl font-black text-white mb-3 tracking-tighter drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]">
                                {title}
                            </h1>
                            <p className="text-slate-400 font-medium text-lg">{subtitle}</p>
                        </div>

                        <div className="relative space-y-8">
                            {children}
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};
